import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const AUTH = {
  headers: {
    Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
  },
};

const OFFENSIVE_POSITIONS = ["QB", "RB", "WR", "TE", "FB", "UNK"];
const DEFENSIVE_POSITIONS = ["CB", "DB", "DE", "DL", "DT", "LB", "S"];
const KICKER_POSITIONS = ["PK", "K"];
const PUNTER_POSITIONS = ["P"];

function PlayerComparePage() {
  // Player 1
  const [p1Search, setP1Search] = useState("");
  const [p1Results, setP1Results] = useState([]);
  const [player1, setPlayer1] = useState(null);
  const [weekly1, setWeekly1] = useState([]);
  const p1Ref = useRef(null);

  // Player 2
  const [p2Search, setP2Search] = useState("");
  const [p2Results, setP2Results] = useState([]);
  const [player2, setPlayer2] = useState(null);
  const [weekly2, setWeekly2] = useState([]);
  const p2Ref = useRef(null);

  // Chart toggles (shared)
  const [showPassYds, setShowPassYds] = useState(true);
  const [showRushYds, setShowRushYds] = useState(true);
  const [showRecYds, setShowRecYds] = useState(true);
  const [showTotalYds, setShowTotalYds] = useState(false);
  const [showPassAtt, setShowPassAtt] = useState(false);
  const [showPassComp, setShowPassComp] = useState(false);
  const [showPassTD, setShowPassTD] = useState(false);
  const [showPassInt, setShowPassInt] = useState(false);
  const [showRushAtt, setShowRushAtt] = useState(false);
  const [showRushTD, setShowRushTD] = useState(false);
  const [showReceptions, setShowReceptions] = useState(false);
  const [showRecTD, setShowRecTD] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  const [showDefTackles, setShowDefTackles] = useState(false);
  const [showDefSacks, setShowDefSacks] = useState(false);
  const [showDefInts, setShowDefInts] = useState(false);
  const [showDefPD, setShowDefPD] = useState(false);
  const [showKickFGM, setShowKickFGM] = useState(false);
  const [showKickFGA, setShowKickFGA] = useState(false);
  const [showPuntYds, setShowPuntYds] = useState(false);
  const [showPunts, setShowPunts] = useState(false);

  const pos1 = player1?.position || player1?.position_abbreviation || "";
  const pos2 = player2?.position || player2?.position_abbreviation || "";

const [p1SelectedIndex, setP1SelectedIndex] = useState(-1);
const [p2SelectedIndex, setP2SelectedIndex] = useState(-1);

const p1JustSelected = useRef(false);
const p2JustSelected = useRef(false);

const [p1Focused, setP1Focused] = useState(false);
const [p2Focused, setP2Focused] = useState(false);

const [season1, setSeason1] = useState(null);
const [season2, setSeason2] = useState(null);

  // Navbar dropdowns
  const [openDropdown, setOpenDropdown] = useState(null); // "teams" | "leaders" | "players" | null
  const teamsRef = useRef(null);
  const leadersRef = useRef(null);
  const playersRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileOpenSection, setMobileOpenSection] = useState(null); // "teams" | "leaders" | "players" | null
  
  // Navbar search
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [navbarSearchTerm, setNavbarSearchTerm] = useState("");
  const [navbarResults, setNavbarResults] = useState([]);
  const [navbarSelectedIndex, setNavbarSelectedIndex] = useState(-1);
  const navbarInputRef = useRef(null);
  const navbarContainerRef = useRef(null);

// Close search + any dropdown when clicking outside
useEffect(() => {
  const handleClickOutside = (e) => {
    // Close expanded search
    if (
      navbarContainerRef.current &&
      !navbarContainerRef.current.contains(e.target)
    ) {
      setSearchExpanded(false);
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }

    // Close dropdowns
    if (
      teamsRef.current && !teamsRef.current.contains(e.target) &&
      leadersRef.current && !leadersRef.current.contains(e.target) &&
      playersRef.current && !playersRef.current.contains(e.target)
    ) {
      setOpenDropdown(null);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);

  // ========== Navbar Search ==========
  useEffect(() => {
    if (!navbarSearchTerm.trim()) {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
      return;
    }

    const search = async () => {
      try {
        const res = await axios.get(
          `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(
            navbarSearchTerm
          )}`,
          AUTH
        );
        setNavbarResults(res.data.slice(0, 12));
        setNavbarSelectedIndex(-1);
      } catch (err) {
        console.error("Navbar search error:", err);
      }
    };

    const timeout = setTimeout(search, 400);
    return () => clearTimeout(timeout);
  }, [navbarSearchTerm]);

  // Close navbar results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        navbarContainerRef.current &&
        !navbarContainerRef.current.contains(e.target)
      ) {
        setNavbarResults([]);
        setNavbarSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

// Keyboard navigation – Navbar
useEffect(() => {
  const handleKeyDown = (e) => {
    if (!navbarResults.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) =>
        Math.min(prev + 1, navbarResults.length - 1)
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setNavbarSelectedIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === "Enter" && navbarSelectedIndex >= 0) {
      const selected = navbarResults[navbarSelectedIndex];
      const path =
        selected.type === "team"
          ? `/team/${selected.abbreviation}`
          : `/player/${selected.bdl_id}`;
      window.location.href = path;
      setNavbarSearchTerm("");
      setNavbarResults([]);
    } else if (e.key === "Escape") {
      setNavbarResults([]);
      setNavbarSelectedIndex(-1);
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [navbarResults, navbarSelectedIndex]);

// Keyboard navigation for both search bars
useEffect(() => {
  const handleKeyDown = (e) => {
    // Player 1
    if (p1Results.length > 0 && document.activeElement === p1Ref.current?.querySelector("input")) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setP1SelectedIndex((prev) => Math.min(prev + 1, p1Results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setP1SelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter" && p1SelectedIndex >= 0) {
        e.preventDefault();
        loadPlayer(p1Results[p1SelectedIndex].bdl_id, 1);
      } else if (e.key === "Escape") {
        setP1Results([]);
        setP1SelectedIndex(-1);
      }
    }

    // Player 2
    if (p2Results.length > 0 && document.activeElement === p2Ref.current?.querySelector("input")) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setP2SelectedIndex((prev) => Math.min(prev + 1, p2Results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setP2SelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === "Enter" && p2SelectedIndex >= 0) {
        e.preventDefault();
        loadPlayer(p2Results[p2SelectedIndex].bdl_id, 2);
      } else if (e.key === "Escape") {
        setP2Results([]);
        setP2SelectedIndex(-1);
      }
    }
  };

  document.addEventListener("keydown", handleKeyDown);
  return () => document.removeEventListener("keydown", handleKeyDown);
}, [p1Results, p1SelectedIndex, p2Results, p2SelectedIndex]);

// Auto-enable relevant stats when players change
useEffect(() => {
  const positions = [pos1, pos2].filter(Boolean);

  // Reset all first
  setShowPassYds(false);
  setShowRushYds(false);
  setShowRecYds(false);
  setShowTotalYds(false);
  setShowPassTD(false);
  setShowRushAtt(false);
  setShowReceptions(false);
  setShowDefTackles(false);
  setShowDefSacks(false);
  setShowDefInts(false);
  setShowKickFGM(false);

  // Always show Total Yards when anyone is selected
  if (positions.length > 0) {
    setShowTotalYds(true);
  }

  positions.forEach((pos) => {
    if (pos === "QB") {
      setShowPassYds(true);
      setShowPassTD(true);
    }
    if (pos === "WR" || pos === "TE") {
      setShowReceptions(true);
      setShowRecYds(true);
      setShowRushYds(true);
    }
    if (pos === "RB" || pos === "FB") {
      setShowRushAtt(true);
      setShowRushYds(true);
      setShowReceptions(true);
      setShowRecYds(true);
    }
    if (["CB", "DB", "DE", "DL", "DT", "LB", "S"].includes(pos)) {
      setShowDefTackles(true);
      setShowDefSacks(true);
      setShowDefInts(true);
    }
    if (pos === "PK" || pos === "K") {
      setShowKickFGM(true);
    }
  });
}, [pos1, pos2]);

// Player 1 search
useEffect(() => {
  if (!p1Search.trim()) {
    setP1Results([]);
    return;
  }
  if (p1JustSelected.current) {
    p1JustSelected.current = false;
    return;
  }

  const t = setTimeout(async () => {
    try {
      const res = await axios.get(
        `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(p1Search)}`,
        AUTH
      );
      setP1Results((res.data || []).filter((p) => p.type !== "team").slice(0, 8));
      setP1SelectedIndex(-1);
    } catch (err) {
      console.error(err);
    }
  }, 350);
  return () => clearTimeout(t);
}, [p1Search]);

// Player 2 search
useEffect(() => {
  if (!p2Search.trim()) {
    setP2Results([]);
    return;
  }
  if (p2JustSelected.current) {
    p2JustSelected.current = false;
    return;
  }

  const t = setTimeout(async () => {
    try {
      const res = await axios.get(
        `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-players?search=${encodeURIComponent(p2Search)}`,
        AUTH
      );
      setP2Results((res.data || []).filter((p) => p.type !== "team").slice(0, 8));
      setP2SelectedIndex(-1);
    } catch (err) {
      console.error(err);
    }
  }, 350);
  return () => clearTimeout(t);
}, [p2Search]);

  // Close search dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (p1Ref.current && !p1Ref.current.contains(e.target)) setP1Results([]);
      if (p2Ref.current && !p2Ref.current.contains(e.target)) setP2Results([]);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

const loadPlayer = async (playerId, slot) => {
  try {
    const [profileRes, weeklyRes, seasonRes] = await Promise.all([
      axios.get(
        `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-player/${playerId}`,
        AUTH
      ),
      axios.get(
        `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-season-player-stats/${playerId}?season=2026`,
        AUTH
      ),
      axios.get(
        `https://pfuzmgbswynewjxsweqd.supabase.co/functions/v1/get-player-season-totals/${playerId}?season=2026`,
        AUTH
      ),
    ]);

    const player = profileRes.data.player || profileRes.data;
    const sorted = (weeklyRes.data || []).sort((a, b) => a.week - b.week);
    const season = seasonRes.data || {};

    if (slot === 1) {
      setPlayer1(player);
      setWeekly1(sorted);
      setSeason1(season);
      setP1Search(`${player.first_name || ""} ${player.last_name || player.name || ""}`.trim());
      setP1Results([]);
      setP1SelectedIndex(-1);
      setP1Focused(false);
    } else {
      setPlayer2(player);
      setWeekly2(sorted);
      setSeason2(season);
      setP2Search(`${player.first_name || ""} ${player.last_name || player.name || ""}`.trim());
      setP2Results([]);
      setP2SelectedIndex(-1);
      setP2Focused(false);
    }
  } catch (err) {
    console.error("Load player error:", err);
  }
};

  // Build combined chart data (weeks 1–18)
  const maxWeek = Math.max(
    weekly1.length ? Math.max(...weekly1.map((s) => s.week)) : 0,
    weekly2.length ? Math.max(...weekly2.map((s) => s.week)) : 0,
    1
  );

  const chartData = [];
  for (let w = 1; w <= maxWeek; w++) {
    const s1 = weekly1.find((s) => s.week === w) || {};
    const s2 = weekly2.find((s) => s.week === w) || {};

    chartData.push({
      week: `W${w}`,
      // Player 1
      "P1 Pass Yds": s1.pass_yards || 0,
      "P1 Rush Yds": s1.rush_yards || 0,
      "P1 Rec Yds": s1.rec_yards || 0,
      "P1 Total Yds": (s1.pass_yards || 0) + (s1.rush_yards || 0) + (s1.rec_yards || 0),
      "P1 Pass Att": s1.pass_attempts || 0,
      "P1 Pass Comp": s1.pass_completions || 0,
      "P1 Pass TD": s1.pass_tds || 0,
      "P1 Pass Int": s1.pass_ints || 0,
      "P1 Rush Att": s1.rush_attempts || 0,
      "P1 Rush TD": s1.rush_tds || 0,
      "P1 Receptions": s1.receptions || 0,
      "P1 Rec TD": s1.rec_tds || 0,
      "P1 Targets": s1.rec_targets || 0,
      "P1 Tackles": s1.solo_tackles || 0,
      "P1 Sacks": s1.def_sacks || 0,
      "P1 Def Int": s1.def_ints || 0,
      "P1 Pass Def": s1.def_pass_defended || 0,
      "P1 FG Made": s1.kick_fgm || 0,
      "P1 FG Att": s1.kick_fga || 0,
      "P1 Punt Yds": s1.punt_yards || 0,
      "P1 Punts": s1.punt_punts || 0,
      // Player 2
      "P2 Pass Yds": s2.pass_yards || 0,
      "P2 Rush Yds": s2.rush_yards || 0,
      "P2 Rec Yds": s2.rec_yards || 0,
      "P2 Total Yds": (s2.pass_yards || 0) + (s2.rush_yards || 0) + (s2.rec_yards || 0),
      "P2 Pass Att": s2.pass_attempts || 0,
      "P2 Pass Comp": s2.pass_completions || 0,
      "P2 Pass TD": s2.pass_tds || 0,
      "P2 Pass Int": s2.pass_ints || 0,
      "P2 Rush Att": s2.rush_attempts || 0,
      "P2 Rush TD": s2.rush_tds || 0,
      "P2 Receptions": s2.receptions || 0,
      "P2 Rec TD": s2.rec_tds || 0,
      "P2 Targets": s2.rec_targets || 0,
      "P2 Tackles": s2.solo_tackles || 0,
      "P2 Sacks": s2.def_sacks || 0,
      "P2 Def Int": s2.def_ints || 0,
      "P2 Pass Def": s2.def_pass_defended || 0,
      "P2 FG Made": s2.kick_fgm || 0,
      "P2 FG Att": s2.kick_fga || 0,
      "P2 Punt Yds": s2.punt_yards || 0,
      "P2 Punts": s2.punt_punts || 0,
    });
  }

  // Determine which checkbox groups to show based on selected players

  const showOffense =
    OFFENSIVE_POSITIONS.includes(pos1) || OFFENSIVE_POSITIONS.includes(pos2);
  const showDefense =
    DEFENSIVE_POSITIONS.includes(pos1) || DEFENSIVE_POSITIONS.includes(pos2);
  const showKicker =
    KICKER_POSITIONS.includes(pos1) || KICKER_POSITIONS.includes(pos2);
  const showPunter =
    PUNTER_POSITIONS.includes(pos1) || PUNTER_POSITIONS.includes(pos2);

  const p1Name = player1
    ? `${player1.first_name || ""} ${player1.last_name || player1.name || ""}`.trim()
    : "Player 1";
  const p2Name = player2
    ? `${player2.first_name || ""} ${player2.last_name || player2.name || ""}`.trim()
    : "Player 2";

const getAdvancedStatsForPosition = (pos) => {
  if (pos === "QB") {
    return [
      { key: "passing_yards", label: "Pass Yds" },
      { key: "passing_touchdowns", label: "Pass TD" },
      { key: "passing_interceptions", label: "INT" },
      { key: "qbr", label: "QBR" },
      { key: "passing_completion_pct", label: "Comp %" },
      { key: "passing_completions", label: "Pass Comp" },
      { key: "passing_attempts", label: "Pass Att" },
      { key: "yards_per_pass_attempt", label: "Y/Pass" },
      { key: "passing_yards_per_game", label: "Pass Yds/GM" },
      { key: "rushing_attempts", label: "Rush Att" },
      { key: "rushing_yards", label: "Rush Yds" },
      { key: "yards_per_rush_attempt", label: "Y/A" },
      { key: "rushing_touchdowns", label: "Rush TD" },
      { key: "rushing_yards_per_game", label: "Rush Yds/GM" },
      { key: "rushing_fumbles", label: "Rush Fum" },
      { key: "rushing_fumbles_lost", label: "Rush Fum Lost" },
      { key: "rushing_first_downs", label: "Rush 1D" },
      { key: "receptions", label: "Rec" },
      { key: "receiving_yards", label: "Rec Yds" },
      { key: "receiving_touchdowns", label: "Rec TD" },
      { key: "receiving_targets", label: "Targets" },
      { key: "receiving_yards_per_game", label: "Rec Y/G" },
      { key: "receiving_first_downs", label: "Rec 1D" },
      { key: "receiving_fumbles", label: "Rec Fum" },
      { key: "receiving_fumbles_lost", label: "Rec Fum Lost" },
      { key: "total_tackles", label: "Tackles" },
    ];
  }
  if (pos === "RB" || pos === "FB") {
    return [
      { key: "rushing_attempts", label: "Rush Att" },
      { key: "rushing_yards", label: "Rush Yds" },
      { key: "yards_per_rush_attempt", label: "Y/A" },
      { key: "rushing_touchdowns", label: "Rush TD" },
      { key: "rushing_yards_per_game", label: "Rush Yds/GM" },
      { key: "rushing_fumbles", label: "Rush Fum" },
      { key: "rushing_fumbles_lost", label: "Rush Fum Lost" },
      { key: "rushing_first_downs", label: "Rush 1D" },
      { key: "receptions", label: "Rec" },
      { key: "receiving_yards", label: "Rec Yds" },
      { key: "receiving_touchdowns", label: "Rec TD" },
      { key: "receiving_targets", label: "Targets" },
      { key: "receiving_yards_per_game", label: "Rec Y/G" },
      { key: "receiving_first_downs", label: "Rec 1D" },
      { key: "receiving_fumbles", label: "Rec Fum" },
      { key: "receiving_fumbles_lost", label: "Rec Fum Lost" },
      { key: "passing_yards", label: "Pass Yds" },
      { key: "passing_touchdowns", label: "Pass TD" },
      { key: "passing_interceptions", label: "INT" },
      { key: "qbr", label: "QBR" },
      { key: "passing_completion_pct", label: "Comp %" },
      { key: "passing_completions", label: "Pass Comp" },
      { key: "passing_attempts", label: "Pass Att" },
      { key: "total_tackles", label: "Tackles" },
    ];
  }
  if (pos === "WR" || pos === "TE") {
    return [
      { key: "receptions", label: "Rec" },
      { key: "receiving_yards", label: "Rec Yds" },
      { key: "receiving_touchdowns", label: "Rec TD" },
      { key: "receiving_targets", label: "Targets" },
      { key: "receiving_yards_per_game", label: "Rec Y/G" },
      { key: "receiving_first_downs", label: "Rec 1D" },
      { key: "receiving_fumbles", label: "Rec Fum" },
      { key: "receiving_fumbles_lost", label: "Rec Fum Lost" },
      { key: "rushing_attempts", label: "Rush Att" },
      { key: "rushing_yards", label: "Rush Yds" },
      { key: "yards_per_rush_attempt", label: "Y/A" },
      { key: "rushing_touchdowns", label: "Rush TD" },
      { key: "rushing_yards_per_game", label: "Rush Yds/GM" },
      { key: "rushing_fumbles", label: "Rush Fum" },
      { key: "rushing_fumbles_lost", label: "Rush Fum Lost" },
      { key: "rushing_first_downs", label: "Rush 1D" },
      { key: "passing_yards", label: "Pass Yds" },
      { key: "passing_touchdowns", label: "Pass TD" },
      { key: "passing_interceptions", label: "INT" },
      { key: "qbr", label: "QBR" },
      { key: "passing_completion_pct", label: "Comp %" },
      { key: "passing_completions", label: "Pass Comp" },
      { key: "passing_attempts", label: "Pass Att" },
      { key: "total_tackles", label: "Tackles" },
    ];
  }
  if (["CB", "DB", "DE", "DL", "DT", "LB", "S"].includes(pos)) {
    return [
      { key: "total_tackles", label: "Tackles" },
      { key: "solo_tackles", label: "Solo" },
      { key: "assist_tackles", label: "Assisted" },
      { key: "defensive_sacks", label: "Sacks" },
      { key: "defensive_sack_yards", label: "Sack Yds" },
      { key: "fumbles_recovered", label: "Fum Rec" },
      { key: "fumbles_forced", label: "Forced Fum" },
      { key: "fumbles_touchdowns", label: "Fum TDs" },
      { key: "defensive_interceptions", label: "INT" },
      { key: "interception_touchdowns", label: "INT TDs" },
    ];
  }
  if (pos === "PK" || pos === "K") {
    return [
      { key: "field_goals_made", label: "FG Made" },
      { key: "field_goal_attempts", label: "FG Att" },
      { key: "field_goal_pct", label: "FG %" },
      { key: "field_goals_made_1_19", label: "FGM 1-19" },
      { key: "field_goals_made_20_29", label: "FGM 20-29" },
      { key: "field_goals_made_30_39", label: "FGM 30-39" },
      { key: "field_goals_made_40_49", label: "FGM 40-49" },
      { key: "field_goals_made_50", label: "FGM 50+" },
      { key: "field_goals_attempts_20_29", label: "FGA 20-29" },
      { key: "field_goals_attempts_30_39", label: "FGA 30-39" },
      { key: "field_goals_attempts_40_49", label: "FGA 40-49" },
      { key: "field_goals_attempts_50", label: "FGA 50+" },
    ];
  }
  // fallback
  return [
    { key: "passing_yards", label: "Pass Yds" },
    { key: "rushing_yards", label: "Rush Yds" },
    { key: "receiving_yards", label: "Rec Yds" },
    { key: "total_tackles", label: "Tackles" },
  ];
};

// Build combined list (Player1 position first, then any extra from Player2)
const buildCompareStats = () => {
  const list1 = pos1 ? getAdvancedStatsForPosition(pos1) : [];
  const list2 = pos2 ? getAdvancedStatsForPosition(pos2) : [];
  const seen = new Set();
  const combined = [];
  [...list1, ...list2].forEach((s) => {
    if (!seen.has(s.key)) {
      seen.add(s.key);
      combined.push(s);
    }
  });
  return combined;
};

const compareStats = buildCompareStats();

const formatVal = (val) => {
  if (val === null || val === undefined) return 0;
  const n = Number(val);
  if (Number.isNaN(n)) return 0;
  // show 1 decimal for rates / averages
  if (Math.abs(n) < 100 && String(val).includes(".")) return n.toFixed(1);
  return n;
};

const getColor = (a, b) => {
  const av = Number(a) || 0;
  const bv = Number(b) || 0;
  if (av > bv) return "text-green-400";
  if (av < bv) return "text-red-400";
  return "text-zinc-300";
};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white font-sans">
      {/* NAVBAR */}
      <nav className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-red-500/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Left: Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/images/HOFLogoSquare.png"
              alt="HOF Logo"
              className="w-12 h-12 object-contain"
            />
            <div>
              <div className="text-2xl font-black tracking-tighter">
                HELMETS OFF
              </div>
              <div className="text-[10px] text-red-400 -mt-1">FOOTBALL</div>
            </div>
          </Link>

          <div className="flex-1 hidden md:block"></div>

{/* Right side */}
<div className="flex items-center gap-4">
  
  {/* Collapsible Search (works on all screen sizes) */}
  <div className="relative" ref={navbarContainerRef}>
    {!searchExpanded ? (
      <button
        onClick={() => {
          setSearchExpanded(true);
          setTimeout(() => navbarInputRef.current?.focus(), 50);
        }}
        className="p-2 text-zinc-400 hover:text-white transition"
        aria-label="Search"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    ) : (
      <div className="relative w-64 sm:w-72">
        <input
          ref={navbarInputRef}
          type="text"
          value={navbarSearchTerm}
          onChange={(e) => setNavbarSearchTerm(e.target.value)}
          placeholder="Search players..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-full py-3 px-5 pl-12 text-sm focus:outline-none focus:border-red-500"
          autoFocus
        />
        <span className="absolute left-4 top-3.5 text-zinc-400">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </span>

        {navbarResults.length > 0 && (
          <div className="absolute mt-2 w-full bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50 max-h-64 overflow-y-auto">
            {navbarResults.map((item, index) => (
              <Link
                key={`${item.type}-${item.bdl_id}`}
                to={item.type === "team" ? `/team/${item.abbreviation}` : `/player/${item.bdl_id}`}
                className={`block px-4 py-3 hover:bg-zinc-800 ${navbarSelectedIndex === index ? "bg-zinc-800" : ""}`}
                onClick={() => {
                  setNavbarSearchTerm("");
                  setNavbarResults([]);
                  setSearchExpanded(false);
                }}
              >
                {item.type === "team" ? (
                  <>
                    <span className="font-medium">{item.name}</span>
                    <span className="text-zinc-500 text-sm"> • Team</span>
                  </>
                ) : (
                  <>
                    {item.first_name} {item.last_name} • {item.position_abbreviation}
                  </>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    )}
  </div>

  {/* Desktop Nav Links */}
  <div className="hidden md:flex items-center gap-8 text-sm font-medium">
    {/* TEAMS */}
    <div className="relative" ref={teamsRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "teams" ? null : "teams")}
        className="hover:text-red-400 transition"
      >
        TEAMS
      </button>
      {openDropdown === "teams" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/teams" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>All Teams</Link>
          <Link to="/teams?conference=AFC" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>AFC</Link>
          <Link to="/teams?conference=NFC" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>NFC</Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="relative" ref={leadersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "leaders" ? null : "leaders")}
        className="hover:text-red-400 transition"
      >
        LEADERS
      </button>
      {openDropdown === "leaders" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/leaderboards" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Season Leaders</Link>
          <Link to="/leaderboards?stat=passing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Passing</Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Rushing</Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Receiving</Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div className="relative" ref={playersRef}>
      <button
        onClick={() => setOpenDropdown(openDropdown === "players" ? null : "players")}
        className="hover:text-red-400 transition"
      >
        PLAYERS
      </button>
      {openDropdown === "players" && (
        <div className="absolute top-full right-0 mt-3 w-48 bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden z-50">
          <Link to="/playerStats" className="block px-5 py-3 hover:bg-zinc-800 font-medium" onClick={() => setOpenDropdown(null)}>Player Stats</Link>
          <Link to="/playerCompare" className="block px-5 py-3 hover:bg-zinc-800" onClick={() => setOpenDropdown(null)}>Player compare</Link>
        </div>
      )}
    </div>
  </div>

  {/* Mobile Hamburger Button */}
  <button
    className="md:hidden p-2 text-zinc-400 hover:text-white"
    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
    aria-label="Menu"
  >
    {mobileMenuOpen ? (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    ) : (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    )}
  </button>
</div>

        </div>

{/* Mobile Menu - collapsible sections */}
{mobileMenuOpen && (
  <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md border-b border-zinc-800 px-6 py-5 z-40">
    
    {/* TEAMS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "teams" ? null : "teams")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        TEAMS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "teams" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "teams" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/teams" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            All Teams
          </Link>
          <Link to="/teams?conference=AFC" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            AFC
          </Link>
          <Link to="/teams?conference=NFC" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            NFC
          </Link>
        </div>
      )}
    </div>

    {/* LEADERS */}
    <div className="border-b border-zinc-800">
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "leaders" ? null : "leaders")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        LEADERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "leaders" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "leaders" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/leaderboards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Season Leaders
          </Link>
          <Link to="/leaderboards?stat=passing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Passing
          </Link>
          <Link to="/leaderboards?stat=rushing_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Rushing
          </Link>
          <Link to="/leaderboards?stat=receiving_yards" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Receiving
          </Link>
        </div>
      )}
    </div>

    {/* PLAYERS */}
    <div>
      <button
        onClick={() =>
          setMobileOpenSection(mobileOpenSection === "players" ? null : "players")
        }
        className="w-full flex items-center justify-between py-4 text-lg font-medium hover:text-red-400 transition"
      >
        PLAYERS
        <span className="text-zinc-500 text-sm">
          {mobileOpenSection === "players" ? "−" : "+"}
        </span>
      </button>

      {mobileOpenSection === "players" && (
        <div className="pb-4 space-y-3 pl-3">
          <Link to="/playerStats" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Stats
          </Link>
          <Link to="/playerCompare" className="block text-base text-zinc-300 hover:text-red-400" onClick={() => { setMobileMenuOpen(false); setMobileOpenSection(null); }}>
            Player Comparison
          </Link>
         </div>
      )}
    </div>

  </div>
)}
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-black text-center mb-12">
            PLAYER COMPARISON
          </h1>

          {/* ===== GRAPH SECTION ===== */}
          <div className="bg-zinc-900 rounded-3xl shadow-2xl p-8 mb-12">
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
              
              {/* LEFT: Two Search Bars */}
              <div className="space-y-6">
                {/* Player 1 Search */}
                <div ref={p1Ref} className="relative">
                  <label className="text-xs font-mono text-red-500 tracking-widest mb-2 block">
                    PLAYER 1
                  </label>
                  <input
  type="text"
  value={p1Search}
  onChange={(e) => {
    setP1Search(e.target.value);
    setPlayer1(null); // clear selection when user starts typing again
  }}
  onFocus={() => setP1Focused(true)}
  onBlur={() => setTimeout(() => setP1Focused(false), 150)}
  placeholder="Search player..."
  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-sm focus:outline-none focus:border-red-500"
/>
                  {player1 && (
                    <div className="mt-2 text-sm text-zinc-400">
                      {p1Name} • {pos1}
                    </div>
                  )}
                  {p1Focused && p1Results.length > 0 && (
  <div className="absolute z-50 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
    {p1Results.map((p, index) => (
      <button
        key={p.bdl_id}
        onMouseDown={(e) => e.preventDefault()} // keeps focus long enough to register click
        onClick={() => loadPlayer(p.bdl_id, 1)}
        className={`w-full text-left px-4 py-3 text-sm ${
          p1SelectedIndex === index ? "bg-zinc-700" : "hover:bg-zinc-800"
        }`}
      >
        {p.first_name} {p.last_name} • {p.position_abbreviation}
      </button>
    ))}
  </div>
)}
                </div>

                {/* Player 2 Search */}
                <div ref={p2Ref} className="relative">
                  <label className="text-xs font-mono text-blue-400 tracking-widest mb-2 block">
                    PLAYER 2
                  </label>
                  <input
  type="text"
  value={p2Search}
  onChange={(e) => {
    setP2Search(e.target.value);
    setPlayer2(null);
  }}
  onFocus={() => setP2Focused(true)}
  onBlur={() => setTimeout(() => setP2Focused(false), 150)}
  placeholder="Search player..."
  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl py-3 px-4 text-sm focus:outline-none focus:border-blue-500"
/>
                  {player2 && (
                    <div className="mt-2 text-sm text-zinc-400">
                      {p2Name} • {pos2}
                    </div>
                  )}
                  {p2Focused && p2Results.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden max-h-56 overflow-y-auto">
                      {p2Results.map((p, index) => (
  <button
    key={p.bdl_id}
    onClick={() => loadPlayer(p.bdl_id, 2)}
    className={`w-full text-left px-4 py-3 text-sm ${
      p2SelectedIndex === index ? "bg-zinc-700" : "hover:bg-zinc-800"
    }`}
  >
    {p.first_name} {p.last_name} • {p.position_abbreviation}
  </button>
))}
                    </div>
                  )}
                </div>

                {/* Quick clear */}
                {(player1 || player2) && (
                  <button
                    onClick={() => {
                      setPlayer1(null);
                      setPlayer2(null);
                      setWeekly1([]);
                      setWeekly2([]);
                      setP1Search("");
                      setP2Search("");
                      setSeason1(null);
                      setSeason2(null);
                    }}
                    className="text-xs text-zinc-500 hover:text-red-400"
                  >
                    Clear both
                  </button>
                )}
              </div>

              {/* RIGHT: Chart */}
              <div>
                <h2 className="text-2xl font-black mb-6 text-center lg:text">
                  Season Trend
                </h2>
                <div className="h-96">
                  {player1 || player2 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis dataKey="week" stroke="#9CA3AF" />
                        <YAxis stroke="#9CA3AF" />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#18181B", border: "none" }}
                        />
                        <Legend />

                        {/* Player 1 lines (red family) */}
                        {showPassYds && player1 && (
                          <Line type="monotone" dataKey="P1 Pass Yds" name={`${p1Name} Pass Yds`} stroke="#ef4444" strokeWidth={3} dot={false} />
                        )}
                        {showRushYds && player1 && (
                          <Line type="monotone" dataKey="P1 Rush Yds" name={`${p1Name} Rush Yds`} stroke="#f97316" strokeWidth={3} dot={false} />
                        )}
                        {showRecYds && player1 && (
                          <Line type="monotone" dataKey="P1 Rec Yds" name={`${p1Name} Rec Yds`} stroke="#eab308" strokeWidth={3} dot={false} />
                        )}
                        {showTotalYds && player1 && (
                          <Line type="monotone" dataKey="P1 Total Yds" name={`${p1Name} Total`} stroke="#f43f5e" strokeWidth={4} dot={false} />
                        )}

                        {/* Player 2 lines (blue family) */}
                        {showPassYds && player2 && (
                          <Line type="monotone" dataKey="P2 Pass Yds" name={`${p2Name} Pass Yds`} stroke="#3b82f6" strokeWidth={3} strokeDasharray="5 5" dot={false} />
                        )}
                        {showRushYds && player2 && (
                          <Line type="monotone" dataKey="P2 Rush Yds" name={`${p2Name} Rush Yds`} stroke="#06b6d4" strokeWidth={3} strokeDasharray="5 5" dot={false} />
                        )}
                        {showRecYds && player2 && (
                          <Line type="monotone" dataKey="P2 Rec Yds" name={`${p2Name} Rec Yds`} stroke="#8b5cf6" strokeWidth={3} strokeDasharray="5 5" dot={false} />
                        )}
                        {showTotalYds && player2 && (
                          <Line type="monotone" dataKey="P2 Total Yds" name={`${p2Name} Total`} stroke="#6366f1" strokeWidth={4} strokeDasharray="5 5" dot={false} />
                        )}

                        {/* Optional extra stats - Player 1 */}
                        {showPassTD && player1 && <Line type="monotone" dataKey="P1 Pass TD" name={`${p1Name} Pass TD`} stroke="#dc2626" strokeWidth={2} dot={false} />}
                        {showRushTD && player1 && <Line type="monotone" dataKey="P1 Rush TD" name={`${p1Name} Rush TD`} stroke="#ea580c" strokeWidth={2} dot={false} />}
                        {showRecTD && player1 && (<Line type="monotone" dataKey="P1 Rec TD" name={`${p1Name} Rec TD`} stroke="#ca8a04" strokeWidth={2} dot={false} />)}                        
                        {showReceptions && player1 && <Line type="monotone" dataKey="P1 Receptions" name={`${p1Name} Rec`} stroke="#ca8a04" strokeWidth={2} dot={false} />}
                        {showDefTackles && player1 && <Line type="monotone" dataKey="P1 Tackles" name={`${p1Name} Tackles`} stroke="#a855f7" strokeWidth={2} dot={false} />}
                        {showDefSacks && player1 && <Line type="monotone" dataKey="P1 Sacks" name={`${p1Name} Sacks`} stroke="#9333ea" strokeWidth={2} dot={false} />}
                       
                        {/* Optional extra stats - Player 2 */}
                        {showPassTD && player2 && <Line type="monotone" dataKey="P2 Pass TD" name={`${p2Name} Pass TD`} stroke="#2563eb" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                        {showRushTD && player2 && <Line type="monotone" dataKey="P2 Rush TD" name={`${p2Name} Rush TD`} stroke="#0891b2" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                        {showRecTD && player2 && (<Line type="monotone" dataKey="P2 Rec TD" name={`${p2Name} Rec TD`} stroke="#7c3aed" strokeWidth={2} strokeDasharray="5 5" dot={false} />)}                          
                        {showReceptions && player2 && <Line type="monotone" dataKey="P2 Receptions" name={`${p2Name} Rec`} stroke="#7c3aed" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                        {showDefTackles && player2 && <Line type="monotone" dataKey="P2 Tackles" name={`${p2Name} Tackles`} stroke="#c084fc" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                        {showDefSacks && player2 && <Line type="monotone" dataKey="P2 Sacks" name={`${p2Name} Sacks`} stroke="#a78bfa" strokeWidth={2} strokeDasharray="5 5" dot={false} />}
                       </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-zinc-500">
                      Select two players to compare
                    </div>
                  )}
                </div>
              </div>
            </div>

{/* Checkboxes - all groups always visible, only relevant ones checked */}
<div className="bg-zinc-800/50 rounded-2xl p-6 mt-8">
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">

    {/* ALL */}
    <div>
      <h4 className="text-sm font-bold text-zinc-400 mb-3 tracking-wider">ALL</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl transition">
          <input type="checkbox" checked={showTotalYds} onChange={() => setShowTotalYds(!showTotalYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Total Yards</span>
        </label>
      </div>
    </div>

    {/* QB */}
    <div>
      <h4 className="text-sm font-bold text-blue-400 mb-3 tracking-wider">QB</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showPassYds} onChange={() => setShowPassYds(!showPassYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Pass Yards</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showPassTD} onChange={() => setShowPassTD(!showPassTD)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Pass TDs</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRushTD} onChange={() => setShowRushTD(!showRushTD)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rush TDs</span>
        </label>        
      </div>
    </div>

    {/* WR / TE */}
    <div>
      <h4 className="text-sm font-bold text-amber-400 mb-3 tracking-wider">WR / TE</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showReceptions} onChange={() => setShowReceptions(!showReceptions)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Receptions</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRecYds} onChange={() => setShowRecYds(!showRecYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rec Yards</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRushYds} onChange={() => setShowRushYds(!showRushYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rush Yards</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
           <input type="checkbox" checked={showRecTD} onChange={() => setShowRecTD(!showRecTD)} className="w-4 h-4 accent-red-500" />
           <span className="text-sm">Rec TDs</span>
        </label>
      </div>
    </div>

    {/* RB / FB */}
    <div>
      <h4 className="text-sm font-bold text-green-400 mb-3 tracking-wider">RB / FB</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRushAtt} onChange={() => setShowRushAtt(!showRushAtt)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rush Attempts</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRushYds} onChange={() => setShowRushYds(!showRushYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rush Yards</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showReceptions} onChange={() => setShowReceptions(!showReceptions)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Receptions</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRecYds} onChange={() => setShowRecYds(!showRecYds)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rec Yards</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showRushTD} onChange={() => setShowRushTD(!showRushTD)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Rush TDs</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
            <input type="checkbox" checked={showRecTD} onChange={() => setShowRecTD(!showRecTD)} className="w-4 h-4 accent-red-500" />
            <span className="text-sm">Rec TDs</span>
        </label>
      </div>
    </div>

    {/* DEFENSE */}
    <div>
      <h4 className="text-sm font-bold text-purple-400 mb-3 tracking-wider">DEFENSE</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showDefTackles} onChange={() => setShowDefTackles(!showDefTackles)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Tackles</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showDefSacks} onChange={() => setShowDefSacks(!showDefSacks)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">Sacks</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showDefInts} onChange={() => setShowDefInts(!showDefInts)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">INTs</span>
        </label>
      </div>
    </div>

    {/* KICKER */}
    <div>
      <h4 className="text-sm font-bold text-emerald-400 mb-3 tracking-wider">KICKER</h4>
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer bg-zinc-900/60 hover:bg-zinc-700 px-3 py-2 rounded-xl">
          <input type="checkbox" checked={showKickFGM} onChange={() => setShowKickFGM(!showKickFGM)} className="w-4 h-4 accent-red-500" />
          <span className="text-sm">FG Made</span>
        </label>
      </div>
    </div>

  </div>
</div>
          </div>

{/* ===== SIDE-BY-SIDE ADVANCED STATS ===== */}
{(player1 || player2) && (
  <div className="bg-zinc-900 rounded-3xl shadow-2xl p-8">
    <h2 className="text-3xl font-black text-center mb-10">
      Season Stats Comparison
    </h2>

    {/* Headers */}
    <div className="grid grid-cols-3 gap-4 mb-6 pb-4 border-b border-zinc-700">
      <div className="text-center text-red-400 font-bold text-lg">
        {p1Name || "Player 1"}
        {pos1 && <span className="block text-xs text-zinc-500 font-normal mt-1">{pos1}</span>}
      </div>
      <div className="text-center text-zinc-500 font-mono text-sm tracking-widest self-center">
        STAT
      </div>
      <div className="text-center text-blue-400 font-bold text-lg">
        {p2Name || "Player 2"}
        {pos2 && <span className="block text-xs text-zinc-500 font-normal mt-1">{pos2}</span>}
      </div>
    </div>

    {/* Stat rows */}
    <div className="space-y-2">
      {compareStats.map((stat) => {
        const v1 = formatVal(season1?.[stat.key]);
        const v2 = formatVal(season2?.[stat.key]);
        return (
          <div
            key={stat.key}
            className="grid grid-cols-3 gap-4 items-center py-3 px-4 rounded-xl hover:bg-zinc-800/50 transition"
          >
            <div className={`text-center text-2xl font-black ${getColor(v1, v2)}`}>
              {player1 ? v1 : "—"}
            </div>
            <div className="text-center text-sm text-zinc-400 font-medium">
              {stat.label}
            </div>
            <div className={`text-center text-2xl font-black ${getColor(v2, v1)}`}>
              {player2 ? v2 : "—"}
            </div>
          </div>
        );
      })}
    </div>

    {(!player1 || !player2) && (
      <p className="text-center text-zinc-500 mt-8 text-sm">
        Select both players to compare full season stats
      </p>
    )}
  </div>
)}


        </div>
      </div>

      <footer className="bg-black border-t border-zinc-800 py-16">
        <div className="max-w-7xl mx-auto px-6 text-center text-zinc-500">
          <p>© 2026 Helmets Off Football • Serratox</p>
        </div>
      </footer>
    </div>
  );
}

export default PlayerComparePage;